import React, { useState, useEffect, useCallback } from 'react';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { fileService, ApiFile, GetFilesParams } from '../services/file.service';
import { DashboardLayout } from '../components/DashboardLayout';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    TextField,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Description as DescriptionIcon, Edit as EditIcon } from '@mui/icons-material';
import { toastService } from '../services/toast.service';
import { UniversalPagination } from '../components/common/UniversalPagination';
import { PageToolbar } from '@/components/common/PageToolbar';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { PageCard } from '@/components/common/PageCard';
import { PageHint } from '@/components/common/PageHint';

const FilesPageContent: React.FC = () => {
    const [files, setFiles] = useState<ApiFile[]>([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadComment, setUploadComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [fileTypeFilter, setFileTypeFilter] = useState<'image' | 'document' | 'other' | ''>('');
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<ApiFile | null>(null);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [fileToEdit, setFileToEdit] = useState<ApiFile | null>(null);
    const [editComment, setEditComment] = useState('');

    const fetchFiles = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: GetFilesParams = {
                limit,
                offset: (page - 1) * limit,
                search: searchQuery || undefined,
                type: fileTypeFilter || undefined,
            };
            const response = await fileService.getFiles(params);
            console.log('DEBUG: Data received in fetchFiles (Files.tsx):', response.files); // Added for debugging
            console.log('DEBUG: Total files received in fetchFiles (Files.tsx):', response.total); // Added for debugging
            setFiles(response.files);
            setTotalFiles(response.total);
        } catch (err) {
          toastService.error('Не вдалося завантажити файли.');
            console.error('Fetch files error:', err);
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchQuery, fileTypeFilter]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        } else {
            setSelectedFile(null);
        }
    };

    const handleOpenUploadModal = () => {
        setUploadModalOpen(true);
        setError(null);
        setSelectedFile(null);
        setUploadComment('');
    };

    const handleCloseUploadModal = () => {
        setUploadModalOpen(false);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            const noFileError = 'Будь ласка, виберіть файл для завантаження.';
            setError(noFileError);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await fileService.uploadFile(selectedFile, uploadComment);
          toastService.success('Файл успішно завантажено!');
            handleCloseUploadModal();
            fetchFiles(); // Refresh the list
        } catch (err: any) {
            // Standardized error handling: rely on ApiError.message from api.ts interceptor
            const errorMessage = err.message || 'Помилка завантаження файлу.';
            setError(errorMessage);
            console.error('Upload error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (file: ApiFile) => {
        setFileToDelete(file);
        setConfirmDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!fileToDelete) return;

        setLoading(true);
        try {
            await fileService.deleteFile(fileToDelete.id);
          toastService.success(`Файл "${fileToDelete.originalFilename}" успішно видалено.`);
            fetchFiles(); // Refresh the list
        } catch (err) {
          toastService.error('Не вдалося видалити файл.');
            console.error('Delete error:', err);
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
            setFileToDelete(null);
        }
    };

    const handleDownload = (file: ApiFile) => {
        try {
            const link = document.createElement('a');
            link.href = file.url;
            link.setAttribute('download', file.originalFilename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
          toastService.error('Не вдалося завантажити файл.');
            console.error('Download error:', err);
        }
    };

    const handleEditClick = (file: ApiFile) => {
        setFileToEdit(file);
        setEditComment(file.comment || '');
        setEditModalOpen(true);
    };

    const handleSaveComment = async () => {
        if (!fileToEdit) return;

        setLoading(true);
        try {
            const updatedFile = await fileService.updateFileComment(fileToEdit.id, editComment);
            setFiles(files.map(f => f.id === updatedFile.id ? updatedFile : f));
          toastService.success('Коментар успішно оновлено.');
            setEditModalOpen(false);
            setFileToEdit(null);
            fetchFiles(); // Refresh the list after successful comment update
        } catch (err) {
          toastService.error('Не вдалося оновити коментар.');
            console.error('Update comment error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (value: number) => {
        setPage(value);
    };

    const handleLimitChange = (value: number) => {
        setLimit(value);
        setPage(1);
    };
    return (
        <Box data-i-doc={PageIndices.FILES}>
          <PageHint iDoc={PageIndices.FILES} />

            <PageCard>
                <PageToolbar
                    storageKey="filters:files"
                    
                    filters={[
                        {
                            id: 'search',
                            type: 'text',
                            label: 'Пошук за назвою',
                            value: searchQuery,
                            onChange: (v) => { setSearchQuery(v); setPage(1); },
                        },
                        {
                            id: 'fileType',
                            type: 'select',
                            label: 'Тип файлу',
                            value: fileTypeFilter,
                            onChange: (v) => { setFileTypeFilter(v); setPage(1); },
                            flexGrow: 1,
                            options: [
                                { value: 'image', label: 'Зображення' },
                                { value: 'document', label: 'Документи' },
                                { value: 'other', label: 'Інші' },
                            ],
                        },
                    ]}
                    onReset={() => { setSearchQuery(''); setFileTypeFilter(''); setPage(1); }}
                    extraActions={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenUploadModal}>
                            Додати
                        </Button>
                    }
                />

                {loading && (!files || files.length === 0) ? (
                    <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>
                ) : (!files || files.length === 0) ? (
                     <Typography sx={{ mt: 4, textAlign: 'center' }}>Файлів не знайдено.</Typography>
                ) : (
                    <List>
                        {files.map((file) => (
                            <ListItem key={file.id} divider disablePadding>
                                <ListItemButton onClick={() => handleDownload(file)}>
                                    <ListItemIcon>
                                        {file.fileType === 'image' ? (
                                            <Avatar variant="square" src={file.url} />
                                        ) : (
                                            <Avatar variant="square"><DescriptionIcon /></Avatar>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.originalFilename}
                                        secondary={
                                            <>
                                                <Typography component="span" variant="body2" color="text.primary">
                                                    {`${file.fileType} - ${(file.size / 1024).toFixed(2)} KB`}
                                                </Typography>
                                                {file.comment && (
                                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                        {file.comment}
                                                    </Typography>
                                                )}
                                            </>
                                        }
                                    />
                                </ListItemButton>
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" onClick={() => handleEditClick(file)} disabled={loading}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton edge="end" onClick={() => handleDeleteClick(file)} disabled={loading}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                )}

                <UniversalPagination
                    page={page}
                    count={Math.ceil(totalFiles / limit)}
                    limit={limit}
                    total={totalFiles}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    storageKey="pagination:files"
                />
            </PageCard>

            {/* Upload Modal */}
            <Dialog open={isUploadModalOpen} onClose={handleCloseUploadModal} fullWidth maxWidth="sm">
                <DialogTitle>Завантажити новий файл</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField type="file" id="file-input" onChange={handleFileChange} fullWidth />
                        <TextField
                            label="Коментар (необов'язково)"
                            value={uploadComment}
                            onChange={(e) => setUploadComment(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                            inputProps={{ maxLength: 1000 }}
                        />
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUploadModal}>Скасувати</Button>
                    <Button onClick={handleUpload} disabled={!selectedFile || loading} variant="contained">
                        {loading ? <CircularProgress size={24} /> : 'Завантажити'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Comment Modal */}
            <Dialog open={isEditModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Редагувати коментар</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>{fileToEdit?.originalFilename}</Typography>
                        <TextField
                            label="Коментар"
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            sx={{ mt: 1 }}
                            placeholder="Додайте або змініть коментар..."
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Скасувати</Button>
                    <Button onClick={handleSaveComment} disabled={loading} variant="contained">
                        {loading ? <CircularProgress size={24} /> : 'Зберегти'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={isConfirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={confirmDelete}
                title="Підтвердити видалення"
                message={`Ви впевнені, що хочете видалити файл "${fileToDelete?.originalFilename}"?`}
                confirmText="Видалити"
            />
        </Box>
    );
};

const FilesPage: React.FC = () => {
    return (
        <DashboardLayout>
            <FilesPageContent />
        </DashboardLayout>
    );
};

export default FilesPage;
